'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  GOAL_BY_KEY,
  GOAL_INDUSTRY_FIT,
  INDUSTRY_KEYS,
  INDUSTRY_LABELS,
  getSubIndustries,
  type GoalKey,
  type IndustryKey,
} from '@webbox/shared-types';
import type { TemplateCatalogItem } from './types';

interface AuthUser {
  readonly id: string | number;
  readonly tenants?: readonly {
    readonly tenant?: string | number | { readonly id?: string | number; readonly name?: string };
    readonly role?: string;
  }[];
}

interface TemplateCatalogProps {
  readonly templates: readonly TemplateCatalogItem[];
}

interface FavoritePreferenceValue {
  readonly favorites?: readonly string[];
}

const FAVORITES_KEY = 'template-favorites';

function resolveTenantId(row: NonNullable<AuthUser['tenants']>[number]): string | null {
  const tenant = row.tenant;
  if (tenant == null) return null;
  if (typeof tenant === 'object') return tenant.id == null ? null : String(tenant.id);
  return String(tenant);
}

function resolveTenantLabel(row: NonNullable<AuthUser['tenants']>[number]): string {
  const tenant = row.tenant;
  if (tenant && typeof tenant === 'object' && typeof tenant.name === 'string') return tenant.name;
  const id = resolveTenantId(row);
  return id ? `テナント ${id}` : 'テナント';
}

function readPreferenceFavorites(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];
  const favorites = (value as FavoritePreferenceValue).favorites;
  if (!Array.isArray(favorites)) return [];
  return favorites.filter((slug): slug is string => typeof slug === 'string');
}

/**
 * Resolves an owner-facing sub-industry label (as authored in
 * `TemplateEntry.subIndustries`) to its wire-contract key
 * (`template-sub-industries.ts`'s `SubIndustryDef.key`), which is what the
 * apply endpoint expects as `subIndustry`. Falls back to the label itself
 * when no match is found (e.g. future catalog entries authored with keys
 * directly) so applying a template never hard-fails on a lookup miss.
 */
function resolveSubIndustryKey(industry: string | undefined, label: string): string {
  if (!industry) return label;
  const defs = getSubIndustries(industry as IndustryKey);
  const match = defs.find((def) => def.label === label || def.key === label);
  return match?.key ?? label;
}

export function TemplateCatalog({ templates }: TemplateCatalogProps) {
  // Composed goal templates (v2 entries with `goal`/`industry`) power both
  // the "叶えたいことから選ぶ" section above and the "一覧から選ぶ" flat grid
  // below (plan §3.4 item 2) — the legacy goal-less catalog has been removed,
  // so the flat grid now simply lists every composed entry.
  const browseTemplates = templates;
  const goalTemplateBySlug = useMemo(() => {
    const map = new Map<string, TemplateCatalogItem>();
    for (const template of templates) {
      if (template.goal && template.industry) {
        map.set(`${template.industry}-${template.goal}`, template);
      }
    }
    return map;
  }, [templates]);

  const [mode, setMode] = useState<'goal' | 'list'>('list');

  const categories = useMemo(
    () => ['すべて', ...Array.from(new Set(browseTemplates.map((template) => template.category)))],
    [browseTemplates],
  );
  const [category, setCategory] = useState('すべて');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [favorites, setFavorites] = useState<readonly string[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [message, setMessage] = useState('');
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  // Goal-picker state.
  const [pickerIndustry, setPickerIndustry] = useState<IndustryKey>('beauty');
  const [pickerGoal, setPickerGoal] = useState<GoalKey | null>(null);
  const [pickerSubIndustry, setPickerSubIndustry] = useState<string>('');

  const memberships = useMemo(() => user?.tenants ?? [], [user]);
  const filteredTemplates = useMemo(
    () =>
      category === 'すべて'
        ? browseTemplates
        : browseTemplates.filter((template) => template.category === category),
    [category, browseTemplates],
  );
  const isAuthenticated = user !== null;

  const availableGoals = useMemo(
    () =>
      GOAL_INDUSTRY_FIT[pickerIndustry]
        .map((key) => GOAL_BY_KEY.get(key))
        .filter((def): def is NonNullable<typeof def> => def != null),
    [pickerIndustry],
  );
  const subIndustryOptions = useMemo(() => getSubIndustries(pickerIndustry), [pickerIndustry]);
  const selectedGoalTemplate = pickerGoal
    ? (goalTemplateBySlug.get(`${pickerIndustry}-${pickerGoal}`) ?? null)
    : null;

  useEffect(() => {
    // Changing industry may invalidate the current goal selection.
    setPickerGoal(null);
    const firstSub = subIndustryOptions[0];
    setPickerSubIndustry(firstSub ? firstSub.key : '');
    // Intentionally depends only on pickerIndustry: reset the sub-industry
    // pick when (and only when) the industry changes.
  }, [pickerIndustry]);

  useEffect(() => {
    let cancelled = false;
    async function loadAuthState() {
      try {
        const res = await fetch('/api/tenant-users/me', { credentials: 'include' });
        const data = (await res.json()) as { user?: AuthUser | null };
        if (cancelled) return;
        const nextUser = data.user?.id == null ? null : data.user;
        setUser(nextUser);
        const firstTenantId = nextUser?.tenants?.map(resolveTenantId).find(Boolean) ?? '';
        setSelectedTenantId(firstTenantId);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    }
    void loadAuthState();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    async function loadFavorites() {
      try {
        const res = await fetch(`/api/payload-preferences/${FAVORITES_KEY}`, {
          credentials: 'include',
        });
        const data = (await res.json()) as { value?: unknown };
        if (!cancelled) setFavorites(readPreferenceFavorites(data.value));
      } catch {
        if (!cancelled) setFavorites([]);
      }
    }
    void loadFavorites();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  function persistFavorites(nextFavorites: readonly string[]) {
    setFavorites(nextFavorites);
    void fetch(`/api/payload-preferences/${FAVORITES_KEY}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: { favorites: nextFavorites } }),
    });
  }

  function toggleFavorite(slug: string) {
    if (!isAuthenticated) return;
    const set = new Set(favorites);
    if (set.has(slug)) {
      set.delete(slug);
      setMessage('お気に入りから外しました。');
    } else {
      set.add(slug);
      setMessage('お気に入りに追加しました。');
    }
    persistFavorites(Array.from(set));
  }

  async function applyTemplate(template: TemplateCatalogItem, subIndustry?: string) {
    if (pendingSlug !== null) return;
    if (!selectedTenantId) {
      setMessage('適用先のテナントを選択してください。');
      return;
    }
    if (template.outcomeStory) {
      const firstLine = template.outcomeStory.split(/(?<=[。！？])/)[0] ?? template.outcomeStory;
      const confirmed = window.confirm(
        `「${template.name}」を適用します。\n\n${firstLine}\n\nよろしいですか？`,
      );
      if (!confirmed) return;
    }
    setPendingSlug(template.slug);
    setMessage('');
    try {
      const body: Record<string, unknown> = {
        templateSlug: template.slug,
        tenantId: selectedTenantId,
      };
      if (subIndustry) body.subIndustry = subIndustry;
      const res = await fetch('/api/templates/apply', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as { message?: string; error?: string };
      setMessage(data.message ?? data.error ?? 'テンプレートを適用しました。');
    } catch {
      setMessage('テンプレートの適用に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setPendingSlug(null);
    }
  }

  const canUseActions = authChecked && isAuthenticated;

  return (
    <div className="lp-template-catalog">
      <div className="lp-template-mode-switch" role="tablist" aria-label="テンプレートの探し方">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'goal'}
          className={mode === 'goal' ? 'is-active' : undefined}
          onClick={() => setMode('goal')}
        >
          叶えたいことから選ぶ
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'list'}
          className={mode === 'list' ? 'is-active' : undefined}
          onClick={() => setMode('list')}
        >
          一覧から選ぶ
        </button>
      </div>

      {message ? (
        <div className="lp-template-message" role="status">
          {message}
        </div>
      ) : null}

      {mode === 'goal' ? (
        <section className="lp-goal-picker" aria-label="叶えたいことから選ぶ">
          <div className="lp-goal-step">
            <h2 className="lp-goal-step-title">1. どんなお店・事務所ですか？</h2>
            <div className="lp-goal-industry-list" role="tablist" aria-label="業種">
              {INDUSTRY_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={pickerIndustry === key}
                  className={pickerIndustry === key ? 'is-active' : undefined}
                  onClick={() => setPickerIndustry(key)}
                >
                  {INDUSTRY_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          <div className="lp-goal-step">
            <h2 className="lp-goal-step-title">2. サイトで一番叶えたいことは？</h2>
            <div className="lp-goal-card-list">
              {availableGoals.map((goalDef) => (
                <button
                  key={goalDef.key}
                  type="button"
                  className={pickerGoal === goalDef.key ? 'lp-goal-card is-active' : 'lp-goal-card'}
                  onClick={() => setPickerGoal(goalDef.key)}
                  aria-pressed={pickerGoal === goalDef.key}
                >
                  <span className="lp-goal-card-label">{goalDef.label}</span>
                  <span className="lp-goal-card-tagline">{goalDef.tagline}</span>
                </button>
              ))}
            </div>
          </div>

          {selectedGoalTemplate ? (
            <div className="lp-goal-detail">
              <div className="lp-goal-detail-media">
                <a
                  href={selectedGoalTemplate.previewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    className="lp-template-thumb"
                    src={selectedGoalTemplate.thumb}
                    alt={`${selectedGoalTemplate.name}のテンプレート画面`}
                    width={1792}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                {subIndustryOptions.length > 0 ? (
                  <div className="lp-goal-subindustry" role="tablist" aria-label="サブ業種">
                    {subIndustryOptions.map((sub) => (
                      <button
                        key={sub.key}
                        type="button"
                        role="tab"
                        aria-selected={pickerSubIndustry === sub.key}
                        className={pickerSubIndustry === sub.key ? 'is-active' : undefined}
                        onClick={() => setPickerSubIndustry(sub.key)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="lp-goal-detail-body">
                <div className="lp-template-card-meta">
                  <span>{selectedGoalTemplate.category}</span>
                  {favorites.includes(selectedGoalTemplate.slug) ? (
                    <span className="lp-template-favorite-badge">お気に入り</span>
                  ) : null}
                </div>
                <h3>{selectedGoalTemplate.name}</h3>

                {selectedGoalTemplate.outcomeStory ? (
                  <p className="lp-goal-outcome-story">{selectedGoalTemplate.outcomeStory}</p>
                ) : (
                  <p>{selectedGoalTemplate.description}</p>
                )}

                {selectedGoalTemplate.concerns && selectedGoalTemplate.concerns.length > 0 ? (
                  <div className="lp-goal-concerns">
                    {selectedGoalTemplate.concerns.map((concern) => (
                      <details key={concern.worry} className="lp-goal-concern">
                        <summary>{concern.worry}</summary>
                        <p>{concern.answer}</p>
                      </details>
                    ))}
                  </div>
                ) : null}

                {selectedGoalTemplate.businessFlow &&
                selectedGoalTemplate.businessFlow.length > 0 ? (
                  <div className="lp-goal-flow">
                    <h4>開業後の運用イメージ</h4>
                    <ol>
                      {selectedGoalTemplate.businessFlow.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : null}

                <ul className="lp-template-feature-list">
                  {selectedGoalTemplate.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                <div className="lp-template-actions lp-goal-detail-actions">
                  <a
                    href={selectedGoalTemplate.previewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    プレビュー
                  </a>
                  {canUseActions ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleFavorite(selectedGoalTemplate.slug)}
                      >
                        {favorites.includes(selectedGoalTemplate.slug)
                          ? 'お気に入り解除'
                          : 'お気に入り'}
                      </button>
                      <button
                        type="button"
                        className="lp-template-apply"
                        disabled={pendingSlug !== null}
                        onClick={() =>
                          applyTemplate(
                            selectedGoalTemplate,
                            resolveSubIndustryKey(selectedGoalTemplate.industry, pickerSubIndustry),
                          )
                        }
                      >
                        {pendingSlug === selectedGoalTemplate.slug ? '適用中' : '適用'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : pickerGoal ? (
            <p className="lp-goal-empty">この組み合わせのテンプレートは準備中です。</p>
          ) : null}
        </section>
      ) : (
        <>
          <div className="lp-template-toolbar" aria-label="テンプレート絞り込み">
            <div className="lp-template-filter" role="tablist" aria-label="カテゴリ">
              {categories.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={category === name}
                  className={category === name ? 'is-active' : undefined}
                  onClick={() => setCategory(name)}
                >
                  {name}
                </button>
              ))}
            </div>

            {authChecked && isAuthenticated ? (
              <div className="lp-template-auth-panel">
                {memberships.length > 0 ? (
                  <label>
                    <span>適用先</span>
                    <select
                      value={selectedTenantId}
                      onChange={(event) => setSelectedTenantId(event.target.value)}
                    >
                      {memberships.map((row) => {
                        const id = resolveTenantId(row);
                        if (!id) return null;
                        return (
                          <option key={id} value={id}>
                            {resolveTenantLabel(row)}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                ) : (
                  <span className="lp-template-auth-note">適用可能なテナントがありません。</span>
                )}
              </div>
            ) : null}
          </div>

          <div className="lp-template-grid" aria-live="polite">
            {filteredTemplates.map((template, index) => {
              const isFavorite = favorites.includes(template.slug);
              return (
                <article key={template.slug} className="lp-template-card">
                  <a href={template.previewHref} target="_blank" rel="noopener noreferrer">
                    <img
                      className="lp-template-thumb"
                      src={template.thumb}
                      alt={`${template.name}のテンプレート画面`}
                      width={1792}
                      height={1024}
                      loading={index < 3 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                      decoding="async"
                    />
                  </a>
                  <div className="lp-template-card-body">
                    <div className="lp-template-card-meta">
                      <span>{template.category}</span>
                      {isFavorite ? (
                        <span className="lp-template-favorite-badge">お気に入り</span>
                      ) : null}
                    </div>
                    <h2>{template.name}</h2>
                    <p>{template.description}</p>
                    <div className="lp-template-fit">{template.fit}</div>
                    <ul className="lp-template-feature-list">
                      {template.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="lp-template-actions">
                    <a href={template.previewHref} target="_blank" rel="noopener noreferrer">
                      プレビュー
                    </a>
                    {canUseActions ? (
                      <>
                        <button type="button" onClick={() => toggleFavorite(template.slug)}>
                          {isFavorite ? 'お気に入り解除' : 'お気に入り'}
                        </button>
                        <button
                          type="button"
                          className="lp-template-apply"
                          disabled={pendingSlug !== null}
                          onClick={() => applyTemplate(template)}
                        >
                          {pendingSlug === template.slug ? '適用中' : '適用'}
                        </button>
                      </>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
